import { useEffect, useState } from 'react';
import {
  UserIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  PaperAirplaneIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { cn } from '../components/ui/utils';
import { ExpandableScreen, ExpandableScreenContent } from '../components/ui/expandable-screen';
import { NewInvoiceForm } from '../components/admin/NewInvoiceForm';
import { DirectBookingForm } from '../components/admin/DirectBookingForm';
import { StreetNightForm } from '../components/admin/StreetNightForm';

interface Client {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  business: string | null;
  notes: string | null;
  created_at: string;
}

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

interface Invoice {
  id: string;
  client_id: string | null;
  invoice_number: string;
  date: string;
  event_date: string | null;
  description: string | null;
  line_items: LineItem[];
  subtotal: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  payment_method: string | null;
  paid_at: string | null;
  created_at: string;
  clients?: Client | null;
  invoice_type?: 'standard' | 'quote' | 'final';
  amount_due?: number | null;
  stripe_payment_link_url?: string | null;
  pdf_token?: string | null;
}

interface InvoicePayment {
  id: string;
  invoice_id: string;
  amount: number;
  method: string | null;
  paid_at: string;
  notes: string | null;
}

const STATUS_CONFIG = {
  paid:    { label: 'PAID',    color: 'text-green-400',  bg: 'bg-green-400/10',  Icon: CheckCircleIcon },
  sent:    { label: 'SENT',    color: 'text-blue-400',   bg: 'bg-blue-400/10',   Icon: ClockIcon },
  draft:   { label: 'DRAFT',   color: 'text-white/40',   bg: 'bg-white/5',       Icon: DocumentTextIcon },
  overdue: { label: 'OVERDUE', color: 'text-red-400',    bg: 'bg-red-400/10',    Icon: ExclamationTriangleIcon },
};

function StatusBadge({ status }: { status: Invoice['status'] }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest', cfg.color, cfg.bg)}>
      <cfg.Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function fmt(date: string) {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtUSD(n: number) {
  return `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

const sumPayments = (list: InvoicePayment[] | undefined) =>
  (list || []).reduce((s, p) => s + Number(p.amount || 0), 0);

/**
 * Records a payment against an existing invoice. Jobs are routinely part-paid —
 * a deposit lands, the balance follows — so this takes an amount rather than
 * flipping the invoice to 'paid'. Per client-documents: the invoice only
 * becomes 'paid' once the full balance is in; until then the money lives in
 * invoice_payments, which is what the revenue tiles actually sum.
 */
function RecordPaymentForm({
  invoice,
  collected,
  onRecorded,
}: {
  invoice: Invoice;
  collected: number;
  onRecorded: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const outstanding = Math.max(0, invoice.total - collected);
  const entered = Math.min(Math.max(parseFloat(amount) || 0, 0), outstanding);
  const settles = entered > 0 && outstanding - entered < 0.005;

  const field = 'w-full bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/20 outline-none focus:bg-white/10 transition-colors';
  const label = 'block text-[9px] text-white/40 uppercase tracking-widest mb-1.5';

  async function save() {
    setErr(null);
    if (entered <= 0) return setErr('Enter an amount greater than zero');
    setSaving(true);
    try {
      const { error: payErr } = await supabase.from('invoice_payments').insert({
        invoice_id: invoice.id,
        amount: entered,
        method: method.trim() || null,
        paid_at: new Date().toISOString(),
        notes: notes.trim() || null,
      });
      if (payErr) throw payErr;

      const { error: invErr } = await supabase
        .from('invoices')
        .update({
          amount_due: Math.max(0, outstanding - entered),
          ...(settles
            ? { status: 'paid', paid_at: new Date().toISOString() }
            : {}),
          ...(method.trim() && !invoice.payment_method ? { payment_method: method.trim() } : {}),
        })
        .eq('id', invoice.id);
      if (invErr) throw invErr;

      setAmount('');
      setMethod('');
      setNotes('');
      onRecorded();
    } catch (e: any) {
      setErr(e?.message || 'Could not record the payment');
    } finally {
      setSaving(false);
    }
  }

  if (outstanding < 0.005) {
    return (
      <p className="text-[10px] uppercase tracking-widest text-green-400/70">
        Paid in full — {fmtUSD(collected)} collected
      </p>
    );
  }

  return (
    <div className="bg-white/[0.02] p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[9px] text-white/20 uppercase tracking-widest">Record a payment</p>
        <p className="text-[10px] font-mono text-white/50">
          {fmtUSD(collected)} of {fmtUSD(invoice.total)} · {fmtUSD(outstanding)} outstanding
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={label}>Amount</span>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={field}
            placeholder="0.00"
          />
        </label>
        <label className="block">
          <span className={label}>Method</span>
          <input
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className={field}
            placeholder="Venmo, Cash, Zelle…"
          />
        </label>
      </div>

      <label className="block">
        <span className={label}>Note (optional)</span>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={field}
          placeholder="e.g. Deposit for wall painting"
        />
      </label>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={save}
          disabled={saving || entered <= 0}
          className="px-4 py-2 text-[10px] font-black uppercase tracking-widest bg-white text-black hover:bg-white/90 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Recording…' : 'Record Payment'}
        </button>
        <button
          onClick={() => setAmount(outstanding.toFixed(2))}
          className="text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-white transition-colors"
        >
          Pay balance ({fmtUSD(outstanding)})
        </button>
        {entered > 0 && (
          <span className="text-[10px] uppercase tracking-widest text-white/40">
            {settles ? 'Settles the invoice' : `Leaves ${fmtUSD(outstanding - entered)}`}
          </span>
        )}
      </div>

      {err && <p className="text-[10px] font-bold uppercase tracking-widest text-red-400">{err}</p>}
    </div>
  );
}

export default function AdminInvoices() {
  const [clients, setClients] = useState<Client[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Record<string, InvoicePayment[]>>({});
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  // Inline PDF viewer is opt-in per invoice — opening a row shouldn't fetch a PDF.
  const [showPdf, setShowPdf] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [directBooking, setDirectBooking] = useState(false);
  const [streetNight, setStreetNight] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [sendingId, setSendingId] = useState<string | null>(null);

  async function sendInvoice(inv: Invoice) {
    if (sendingId) return;
    const clientEmail = (inv.clients as any)?.email || '';
    const promptMsg = clientEmail
      ? `Send ${inv.invoice_number} to ${clientEmail}?\n\n(Leave blank to use that address, or enter a different email to override.)`
      : `No email on file for this client. Enter an email to send ${inv.invoice_number} to:`;
    const override = window.prompt(promptMsg, clientEmail);
    if (override === null) return;
    const to_email = override.trim() || clientEmail;
    if (!to_email) {
      alert('No recipient email provided.');
      return;
    }
    setSendingId(inv.id);
    try {
      const res = await fetch('/api/invoices/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoice_id: inv.id, to_email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        alert(`Failed to send invoice: ${data.error || res.statusText}`);
      } else {
        alert(`Invoice ${inv.invoice_number} sent to ${data.sentTo}`);
        load();
      }
    } catch (e: any) {
      alert(`Error sending invoice: ${e?.message || e}`);
    } finally {
      setSendingId(null);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [clientsRes, invoicesRes] = await Promise.all([
        supabase.from('clients').select('*').order('created_at', { ascending: false }),
        supabase.from('invoices').select('*, clients(*)').order('date', { ascending: false }),
      ]);

      const clientList: Client[] = clientsRes.data || [];
      const invoiceList: Invoice[] = (invoicesRes.data || []).map((inv: any) => ({
        ...inv,
        line_items: Array.isArray(inv.line_items) ? inv.line_items : [],
        subtotal: Number(inv.subtotal || 0),
        total: Number(inv.total || 0),
      }));

      setClients(clientList);
      setInvoices(invoiceList);
      // The detail screen holds its own copy of the row; without this it keeps
      // showing the pre-payment status and balance after a payment is recorded.
      setSelectedInvoice(prev => (prev ? invoiceList.find(i => i.id === prev.id) || prev : prev));

      // Fetch payments for all invoices
      if (invoiceList.length > 0) {
        const ids = invoiceList.map(i => i.id);
        const { data: payData } = await supabase
          .from('invoice_payments')
          .select('*')
          .in('invoice_id', ids)
          .order('paid_at', { ascending: true });

        const byInvoice: Record<string, InvoicePayment[]> = {};
        for (const p of payData || []) {
          if (!byInvoice[p.invoice_id]) byInvoice[p.invoice_id] = [];
          byInvoice[p.invoice_id].push(p);
        }
        setPayments(byInvoice);
      }
    } finally {
      setLoading(false);
    }
  }

  const paidInvoices  = invoices.filter(i => i.status === 'paid');
  // Collected follows money actually recorded, so a deposit on a part-paid job
  // counts. Legacy 'paid' invoices predate invoice_payments and have no rows,
  // so they fall back to their total — same rule as the revenue tiles.
  const totalRevenue  = invoices.reduce((s, i) => {
    const recorded = sumPayments(payments[i.id]);
    return s + (recorded > 0 ? recorded : i.status === 'paid' ? i.total : 0);
  }, 0);
  const pending       = invoices.filter(i => i.status === 'sent' || i.status === 'overdue');
  const pendingTotal  = pending.reduce(
    (s, i) => s + Math.max(0, i.total - sumPayments(payments[i.id])),
    0
  );

  function toggleClient(id: string) {
    setExpandedClients(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const clientInvoices = (clientId: string) => invoices.filter(i => i.client_id === clientId);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 pb-8 space-y-10">

      {/* ── Revenue Summary ────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] text-white/30 uppercase tracking-[0.2em]">Revenue Summary</p>
          <div className="flex items-center gap-2">
          <button data-tour="inv-new"
            className="flex items-center gap-2 px-3 py-1.5 bg-white text-black text-[10px] font-bold uppercase tracking-widest hover:bg-white/90 transition-colors"
            onClick={() => setCreatingInvoice(true)}
          >
            <PlusCircleIcon className="w-3.5 h-3.5" />
            New Invoice
          </button>
          <button data-tour="inv-direct"
            className="flex items-center gap-2 px-3 py-1.5 bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-white/20 transition-colors"
            onClick={() => setDirectBooking(true)}
          >
            <PlusCircleIcon className="w-3.5 h-3.5" />
            Direct Booking
          </button>
          <button data-tour="inv-street"
            className="flex items-center gap-2 px-3 py-1.5 bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-white/20 transition-colors"
            onClick={() => setStreetNight(true)}
          >
            <PlusCircleIcon className="w-3.5 h-3.5" />
            Street Night
          </button>
          </div>
        </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
            {[
              { label: 'Total Collected',  value: fmtUSD(totalRevenue),   sub: `${paidInvoices.length} paid` },
              { label: 'Pending',          value: fmtUSD(pendingTotal),   sub: `${pending.length} outstanding` },
              { label: 'Total Invoices',   value: invoices.length,        sub: 'all time' },
              { label: 'Clients',          value: clients.length,         sub: 'on record' },
            ].map(({ label, value, sub }) => (
              <div key={label} className="bg-black px-6 py-5">
                <p className="text-[9px] text-white/30 uppercase tracking-widest mb-1">{label}</p>
                <p className="text-2xl font-black font-mono text-white">{value}</p>
                <p className="text-[9px] text-white/20 mt-1">{sub}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Invoice List ───────────────────────────────────────────────── */}
        <section>
          <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-4">All Invoices</p>
          {loading ? (
            <div className="py-16 text-center text-white/20 text-xs uppercase tracking-widest">Loading…</div>
          ) : invoices.length === 0 ? (
            <div className="py-16 text-center text-white/20 text-xs uppercase tracking-widest">No invoices yet.</div>
          ) : (
            <div className="space-y-2">
              {invoices.map(inv => (
                <div data-tour="inv-row"
                  key={inv.id}
                  className={cn(
                    'flex flex-col gap-5 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] transition-colors',
                    selectedInvoice?.id === inv.id && 'bg-white/[0.07]'
                  )}
                  onClick={() => { setShowPdf(false); setSelectedInvoice(selectedInvoice?.id === inv.id ? null : inv); }}
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <DocumentTextIcon className="w-5 h-5 text-white/20 shrink-0 mt-0.5" />
                    <div className="min-w-0 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-bold font-mono">{inv.invoice_number}</span>
                        <StatusBadge status={inv.status} />
                      </div>
                      <p className="text-[10px] leading-relaxed text-white/40">
                        <span className="uppercase">{(inv.clients as any)?.name || 'Unknown Client'}</span> &nbsp;·&nbsp; {fmt(inv.date)}
                        {inv.event_date && <> &nbsp;·&nbsp; Event: {fmt(inv.event_date)}</>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:justify-end sm:gap-5 shrink-0">
                    <div className="sm:text-right">
                      <p className="text-lg font-black font-mono text-white leading-none">{fmtUSD(inv.total)}</p>
                      {(() => {
                        const collected = sumPayments(payments[inv.id]);
                        const due = inv.total - collected;
                        return collected > 0 && due > 0.005 ? (
                          <p className="text-[9px] text-amber-400/70 uppercase tracking-wider mt-1.5 font-mono">
                            {fmtUSD(collected)} paid · {fmtUSD(due)} due
                          </p>
                        ) : (
                          <p className="text-[9px] text-white/20 uppercase tracking-wider mt-1.5">
                            {inv.payment_method || '—'}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2">
                    {inv.pdf_token && (
                      <a
                        href={`/api/invoices/pdf?id=${inv.id}&token=${inv.pdf_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="View invoice PDF"
                        className="flex items-center gap-1.5 px-3 py-2 text-[9px] font-bold uppercase tracking-widest bg-white/5 hover:bg-white text-white hover:text-black transition-colors"
                      >
                        <DocumentArrowDownIcon className="w-3 h-3" />
                        PDF
                      </a>
                    )}
                    <button data-tour="inv-send"
                      onClick={(e) => { e.stopPropagation(); sendInvoice(inv); }}
                      disabled={sendingId === inv.id}
                      title="Send invoice email to client"
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 text-[9px] font-bold uppercase tracking-widest transition-colors',
                        sendingId === inv.id
                          ? 'bg-white/5 text-white/30 cursor-wait'
                          : 'bg-white/5 hover:bg-white text-white hover:text-black'
                      )}
                    >
                      <PaperAirplaneIcon className="w-3 h-3" />
                      {sendingId === inv.id ? 'Sending…' : 'Send'}
                    </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Invoice Detail — fullscreen ExpandableScreen pattern ─────────── */}
        <ExpandableScreen isOpen={!!selectedInvoice} onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}>
          <ExpandableScreenContent className="overflow-x-hidden">
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className="max-w-3xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16">
                {selectedInvoice && (
                  <div className="space-y-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-3">
                          <h2 className="text-xl font-black font-mono tracking-tighter">
                            {selectedInvoice.invoice_number}
                          </h2>
                          <StatusBadge status={selectedInvoice.status} />
                        </div>
                        <p className="text-[10px] text-white/30 mt-1 uppercase tracking-widest">
                          {selectedInvoice.description}
                        </p>
                      </div>
                      {selectedInvoice.pdf_token && (
                        <div className="flex items-center gap-3 shrink-0">
                          <button data-tour="inv-pdf"
                            onClick={() => setShowPdf(true)}
                            className="flex items-center gap-1.5 px-3 py-2 text-[9px] font-bold uppercase tracking-widest bg-white text-black hover:bg-white/90 transition-colors"
                          >
                            <DocumentTextIcon className="w-3 h-3" />
                            View Invoice
                          </button>
                          <a
                            href={`/api/invoices/pdf?id=${selectedInvoice.id}&token=${selectedInvoice.pdf_token}`}
                            download={`${selectedInvoice.invoice_number}.pdf`}
                            className="flex items-center gap-1.5 px-3 py-2 text-[9px] font-bold uppercase tracking-widest bg-white/5 hover:bg-white text-white hover:text-black transition-colors"
                          >
                            <DocumentArrowDownIcon className="w-3 h-3" />
                            Download
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px]">
                      {[
                        { label: 'Invoice Date', value: fmt(selectedInvoice.date) },
                        { label: 'Event Date',   value: selectedInvoice.event_date ? fmt(selectedInvoice.event_date) : '—' },
                        { label: 'Client',       value: ((selectedInvoice.clients as any)?.name || '—').toUpperCase() },
                        { label: 'Paid On',      value: selectedInvoice.paid_at ? fmt(selectedInvoice.paid_at) : '—' },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-white/5 p-3">
                          <p className="text-[9px] text-white/20 uppercase tracking-widest mb-0.5">{label}</p>
                          <p className="text-white/80">{value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Line Items */}
                    <div>
                      <p className="text-[9px] text-white/20 uppercase tracking-widest mb-2">Line Items</p>
                      <div className="divide-y divide-white/5 bg-white/[0.02] px-4">
                        {selectedInvoice.line_items.map((item, i) => (
                          <div key={i} className="flex justify-between py-3 text-sm">
                            <span className="text-white/70">{item.description}</span>
                            <span className="font-mono font-bold text-white">{fmtUSD(item.amount)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between pt-4 mt-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Total</span>
                        <span className="text-xl font-black font-mono text-green-400">{fmtUSD(selectedInvoice.total)}</span>
                      </div>
                    </div>

                    {/* Payments */}
                    {payments[selectedInvoice.id]?.length > 0 && (
                      <div>
                        <p className="text-[9px] text-white/20 uppercase tracking-widest mb-2">Payments Received</p>
                        <div className="divide-y divide-white/5 bg-white/[0.02] px-4">
                          {payments[selectedInvoice.id].map(p => (
                            <div key={p.id} className="flex justify-between py-3 text-sm">
                              <div>
                                <p className="text-white/70">{p.method || 'Payment'}</p>
                                <p className="text-[9px] text-white/30">{fmt(p.paid_at)}{p.notes && ` · ${p.notes}`}</p>
                              </div>
                              <span className="font-mono font-bold text-green-400">{fmtUSD(p.amount)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between pt-3">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Balance</span>
                          <span className="font-mono font-bold text-white">
                            {fmtUSD(Math.max(0, selectedInvoice.total - sumPayments(payments[selectedInvoice.id])))}
                          </span>
                        </div>
                      </div>
                    )}

                    <RecordPaymentForm
                      invoice={selectedInvoice}
                      collected={sumPayments(payments[selectedInvoice.id])}
                      onRecorded={load}
                    />

                    {/* Payment methods note */}
                    {selectedInvoice.payment_method && (
                      <p className="text-[10px] text-white/30 uppercase tracking-widest">
                        Accepted: {selectedInvoice.payment_method}
                      </p>
                    )}

                  </div>
                )}
              </div>
            </div>
          </ExpandableScreenContent>
        </ExpandableScreen>

        {/* New invoice — full-screen creation form */}
        <ExpandableScreen isOpen={creatingInvoice} onOpenChange={(open) => { if (!open) setCreatingInvoice(false); }}>
          <ExpandableScreenContent className="overflow-x-hidden">
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {creatingInvoice && (
                <NewInvoiceForm
                  clients={clients}
                  onCreated={() => { setCreatingInvoice(false); load(); }}
                  onCancel={() => setCreatingInvoice(false)}
                />
              )}
            </div>
          </ExpandableScreenContent>
        </ExpandableScreen>

        {/* Direct booking — a job taken by phone/email, split like a website booking */}
        <ExpandableScreen isOpen={directBooking} onOpenChange={(open) => { if (!open) setDirectBooking(false); }}>
          <ExpandableScreenContent className="overflow-x-hidden">
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {directBooking && (
                <DirectBookingForm
                  onDone={() => { setDirectBooking(false); load(); }}
                  onCancel={() => setDirectBooking(false)}
                />
              )}
            </div>
          </ExpandableScreenContent>
        </ExpandableScreen>

        {/* Street night — end-of-night walk-up totals, 10% owner pay */}
        <ExpandableScreen isOpen={streetNight} onOpenChange={(open) => { if (!open) setStreetNight(false); }}>
          <ExpandableScreenContent className="overflow-x-hidden">
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {streetNight && (
                <StreetNightForm
                  onDone={() => setStreetNight(false)}
                  onCancel={() => setStreetNight(false)}
                />
              )}
            </div>
          </ExpandableScreenContent>
        </ExpandableScreen>

        {/* Invoice document — rendered as HTML from the invoice record rather
            than embedding the PDF. An <iframe> to the PDF endpoint relies on a
            browser PDF plugin, which isn't always present (it renders blank in
            embedded/webview browsers). Drawing it ourselves always displays;
            Download still serves the real PDF. */}
        <ExpandableScreen isOpen={showPdf} onOpenChange={(open) => { if (!open) setShowPdf(false); }}>
          <ExpandableScreenContent className="overflow-x-hidden">
            {selectedInvoice && (
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="max-w-3xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
                    <div className="flex items-center gap-3 min-w-0">
                      <h2 className="text-lg font-black font-mono tracking-tighter truncate">
                        {selectedInvoice.invoice_number}
                      </h2>
                      <StatusBadge status={selectedInvoice.status} />
                    </div>
                    {selectedInvoice.pdf_token && (
                      <a
                        href={`/api/invoices/pdf?id=${selectedInvoice.id}&token=${selectedInvoice.pdf_token}`}
                        download={`${selectedInvoice.invoice_number}.pdf`}
                        className="flex items-center gap-1.5 px-3 py-2 text-[9px] font-bold uppercase tracking-widest bg-white/5 hover:bg-white text-white hover:text-black transition-colors shrink-0"
                      >
                        <DocumentArrowDownIcon className="w-3 h-3" />
                        Download PDF
                      </a>
                    )}
                  </div>

                  {/* Paper */}
                  <div className="bg-white text-black p-8 sm:p-12">
                    <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
                      <div>
                        <p className="text-[9px] uppercase tracking-[0.25em] text-black/40 mb-1">From</p>
                        <p className="text-sm font-black uppercase">THE LOST+UNFOUNDS</p>
                        <p className="text-xs text-black/60">media@thelostandunfounds.com</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] uppercase tracking-[0.25em] text-black/40 mb-1">Invoice</p>
                        <p className="text-sm font-black font-mono">{selectedInvoice.invoice_number}</p>
                        <p className="text-xs text-black/60">{fmt(selectedInvoice.date)}</p>
                      </div>
                    </div>

                    <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
                      <div>
                        <p className="text-[9px] uppercase tracking-[0.25em] text-black/40 mb-1">Bill To</p>
                        <p className="text-sm font-bold uppercase">
                          {(selectedInvoice.clients as any)?.name || 'Client'}
                        </p>
                        {(selectedInvoice.clients as any)?.email && (
                          <p className="text-xs text-black/60">{(selectedInvoice.clients as any).email}</p>
                        )}
                      </div>
                      {selectedInvoice.event_date && (
                        <div className="text-right">
                          <p className="text-[9px] uppercase tracking-[0.25em] text-black/40 mb-1">Event</p>
                          <p className="text-xs text-black/70">{fmt(selectedInvoice.event_date)}</p>
                        </div>
                      )}
                    </div>

                    {selectedInvoice.description && (
                      <p className="text-xs text-black/70 mb-6">{selectedInvoice.description}</p>
                    )}

                    <table className="w-full text-sm mb-8">
                      <thead>
                        <tr className="border-b border-black/10">
                          <th className="text-left py-2 text-[9px] uppercase tracking-[0.25em] text-black/40 font-normal">Description</th>
                          <th className="text-right py-2 text-[9px] uppercase tracking-[0.25em] text-black/40 font-normal">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.line_items.map((item, i) => (
                          <tr key={i} className="border-b border-black/5">
                            <td className="py-3 pr-4">{item.description}</td>
                            <td className="py-3 text-right font-mono whitespace-nowrap">{fmtUSD(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="flex justify-end">
                      <div className="w-full max-w-[240px]">
                        <div className="flex justify-between py-2 border-t-2 border-black">
                          <span className="text-[10px] font-black uppercase tracking-widest">Total</span>
                          <span className="font-mono font-black">{fmtUSD(selectedInvoice.total)}</span>
                        </div>
                        {payments[selectedInvoice.id]?.length > 0 && (
                          <>
                            {payments[selectedInvoice.id].map(p => (
                              <div key={p.id} className="flex justify-between py-1 text-xs text-black/60">
                                <span>{p.method || 'Payment'} · {fmt(p.paid_at)}</span>
                                <span className="font-mono">−{fmtUSD(p.amount)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between py-2 border-t border-black/10 text-sm">
                              <span className="text-[10px] font-black uppercase tracking-widest">Balance</span>
                              <span className="font-mono font-black">
                                {fmtUSD(Math.max(0, selectedInvoice.total - payments[selectedInvoice.id].reduce((s, p) => s + Number(p.amount || 0), 0)))}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ExpandableScreenContent>
        </ExpandableScreen>

        {/* ── Clients ───────────────────────────────────────────────────── */}
        <section>
          <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-4">Clients</p>
          {loading ? (
            <div className="py-8 text-center text-white/20 text-xs uppercase tracking-widest">Loading…</div>
          ) : clients.length === 0 ? (
            <div className="py-8 text-center text-white/20 text-xs uppercase tracking-widest">No clients yet.</div>
          ) : (
            <div className="space-y-2">
              {clients.map(client => {
                const cInvoices = clientInvoices(client.id);
                const isExpanded = expandedClients.has(client.id);
                const clientTotal = cInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0);
                return (
                  <div key={client.id} className="bg-white/[0.02]">
                    <div
                      className="flex items-start justify-between gap-5 px-5 py-5 cursor-pointer hover:bg-white/[0.03] transition-colors"
                      onClick={() => toggleClient(client.id)}
                    >
                      <div className="flex items-start gap-4 min-w-0">
                        {isExpanded
                          ? <ChevronDownIcon className="w-4 h-4 text-white/30 shrink-0 mt-0.5" />
                          : <ChevronRightIcon className="w-4 h-4 text-white/30 shrink-0 mt-0.5" />}
                        <div className="min-w-0 space-y-2">
                          <p className="text-sm font-bold uppercase">{client.name}</p>
                          <p className="text-[10px] leading-relaxed text-white/30">
                            {client.business && <>{client.business} &nbsp;·&nbsp;</>}
                            {cInvoices.length} invoice{cInvoices.length !== 1 && 's'}
                            {client.email && <> &nbsp;·&nbsp; {client.email}</>}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono font-bold text-white leading-none">{fmtUSD(clientTotal)}</p>
                        <p className="text-[9px] text-white/20 uppercase tracking-wider mt-1.5">collected</p>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="bg-white/[0.015] px-6 pb-4 space-y-2">
                        {client.notes && (
                          <p className="text-[11px] text-white/40 pt-3 italic">{client.notes}</p>
                        )}
                        {cInvoices.length === 0 ? (
                          <p className="text-[11px] text-white/20 pt-3">No invoices for this client.</p>
                        ) : (
                          <div className="pt-2 space-y-1">
                            {cInvoices.map(inv => (
                              <div
                                key={inv.id}
                                className="flex items-center justify-between gap-4 py-3 cursor-pointer hover:bg-white/5 px-3 transition-colors"
                                onClick={(e) => { e.stopPropagation(); setSelectedInvoice(inv); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                              >
                                <div className="flex items-center gap-3 flex-wrap min-w-0">
                                  <span className="text-[10px] font-mono text-white/50">{inv.invoice_number}</span>
                                  <StatusBadge status={inv.status} />
                                  <span className="text-[10px] text-white/30">{fmt(inv.date)}</span>
                                </div>
                                <span className="font-mono text-sm font-bold">{fmtUSD(inv.total)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

    </div>
  );
}
